/**
 * หน้าถวายทรัพย์ (?view=offering[&dn=DN...][&pay=qr|transfer]) — logic ล้วน ทดสอบได้
 * ไม่มี dn = ลูกค้าเลือก/กรอกยอดเองแล้วสร้างใบ (donationSubmit action offeringCreate)
 * ข้อมูลมาจาก donations/{DN} (Admin repo: functions/src/donation/donationOffering.js)
 * เก็บเฉพาะชื่อผู้ร่วมถวาย — ส่งผ่าน donationSubmit เดิม
 */

export type OfferingPay = 'qr' | 'transfer'
export type OfferingDoc = {
  donationId: string
  amount: number
  title: string
  paymentMethod: 'beam' | 'manual_transfer' | 'both'
  status: string
  paid: boolean
  done: boolean
  donorName: string
  lineDisplayName: string
  bank: { name: string; account: string; holder: string; promptPay: string } | null
  hasSlip: boolean
  hasLine: boolean
  botId: string
  offeringAt: string
  confirmedAt: string
}

export type OfferingPublicConfig = {
  enabled: boolean
  title: string
  detail: string
  quickAmounts: number[]
  minAmount: number
  maxAmount: number
  paymentMethod: 'beam' | 'manual_transfer' | 'both'
}

function readParams(search: string): URLSearchParams {
  const sp = new URLSearchParams(search)
  let state = sp.get('liff.state') || ''
  if (/%3[fd]/i.test(state)) state = decodeURIComponent(state)
  if (state.startsWith('?')) state = state.slice(1)
  const st = new URLSearchParams(state)
  st.forEach((v, k) => { if (!sp.has(k)) sp.set(k, v) })
  return sp
}

/** ลิงก์หน้าถวายทรัพย์ในเว็บเดียวกัน (ไม่มี dn = เลือกยอดเอง) */
export function offeringUrl(botId: string, dn = ''): string {
  const sp = new URLSearchParams({ view: 'offering' })
  if (dn) sp.set('dn', dn)
  if (botId === '2') sp.set('botId', '2')
  return `/?${sp}`
}

/** null = ไม่ใช่หน้าถวายทรัพย์ · dn '' = หน้าเลือกยอดเอง */
export function readOfferingParams(search: string): { dn: string; pay: OfferingPay | '' } | null {
  try {
    const sp = readParams(search)
    if (sp.get('view') !== 'offering') return null
    const dn = String(sp.get('dn') || '').replace(/[^A-Za-z0-9-]/g, '')
    if (sp.has('dn') && !/^DN/i.test(dn)) return null
    const pay = sp.get('pay')
    return { dn, pay: pay === 'qr' || pay === 'transfer' ? pay : '' }
  } catch {
    return null
  }
}

const PAID = ['ชำระสำเร็จ', 'ยืนยันแล้ว']

export function parseOfferingDoc(id: string, d: Record<string, unknown> | null | undefined): OfferingDoc | null {
  if (!d) return null
  const status = String(d.status || d.Status || '')
  const pm = String(d.paymentMethod || 'both')
  const b = d.bank && typeof d.bank === 'object' ? d.bank as Record<string, unknown> : null
  return {
    donationId: String(d.donationId || id),
    amount: Number(d.amount) || 0,
    title: String(d.programName || 'ถวายทรัพย์'),
    paymentMethod: pm === 'beam' || pm === 'manual_transfer' ? pm : 'both',
    status,
    paid: !!d.PaidAt || PAID.includes(status),
    done: status === 'ยืนยันแล้ว' || status === 'รอยืนยัน',
    donorName: String(d.donorName || ''),
    lineDisplayName: String(d.lineDisplayName || ''),
    bank: b && b.account ? { name: String(b.name || ''), account: String(b.account || ''), holder: String(b.holder || ''), promptPay: String(b.promptPay || '') } : null,
    hasSlip: !!d.slipUrl,
    hasLine: !!d.lineUserId,
    botId: String(d.fromBot || '') === '2' ? '2' : '1',
    offeringAt: String(d.offeringAt || d.createdAt || ''),
    confirmedAt: String(d.confirmedAt || d.PaidAt || ''),
  }
}

/** แท็บที่เปิดได้ตามวิธีชำระที่แอดมินตั้ง */
export function availablePays(doc: Pick<OfferingDoc, 'paymentMethod'>): OfferingPay[] {
  if (doc.paymentMethod === 'beam') return ['qr']
  if (doc.paymentMethod === 'manual_transfer') return ['transfer']
  return ['qr', 'transfer']
}

export function initialPay(doc: Pick<OfferingDoc, 'paymentMethod'>, wanted: OfferingPay | ''): OfferingPay {
  const list = availablePays(doc)
  return wanted && list.includes(wanted) ? wanted : list[0]
}

export function cleanDonorName(v: string): string {
  return String(v || '').replace(/\s+/g, ' ').trim().slice(0, 80)
}

/**
 * ส่งได้เมื่อไหร่ — QR: ต้องชำระแล้ว (ไม่งั้น donationSubmit จะปิดรายการเป็น "ยืนยันแล้ว" ทั้งที่ยังไม่จ่าย)
 * โอน: ต้องมีสลิป · ทั้งสองต้องมีชื่อ
 */
export function submitBlocker(doc: OfferingDoc, pay: OfferingPay, name: string, slipReady: boolean): string {
  if (doc.done) return 'บันทึกแล้ว'
  if (!cleanDonorName(name)) return 'กรุณาระบุชื่อผู้ร่วมถวาย'
  if (pay === 'qr' && !doc.paid) return 'สแกน QR ชำระให้เรียบร้อยก่อน'
  if (pay === 'transfer' && !doc.paid && !slipReady) return 'แนบสลิปการโอนก่อน'
  return ''
}

/** ข้อมูลส่ง donationSubmit (เก็บชื่ออย่างเดียว) */
export function buildSubmitBody(doc: OfferingDoc, pay: OfferingPay, name: string, slipUrl: string): Record<string, unknown> {
  const body: Record<string, unknown> = {
    donationId: doc.donationId,
    donorName: cleanDonorName(name),
    paymentMethod: pay === 'qr' || doc.paid ? 'beam' : 'manual_transfer',
  }
  if (pay === 'transfer' && slipUrl) body.slipUrl = slipUrl
  return body
}

/** วันเวลาไทย เช่น "15 ต.ค. 2569 · 10:10 น." */
export function thaiDateTime(at: string): string {
  const d = new Date(at)
  if (!at || Number.isNaN(d.getTime())) return '-'
  const date = d.toLocaleDateString('th-TH', { timeZone: 'Asia/Bangkok', day: 'numeric', month: 'short', year: 'numeric' })
  const time = d.toLocaleTimeString('th-TH', { timeZone: 'Asia/Bangkok', hour: '2-digit', minute: '2-digit', hour12: false })
  return `${date} · ${time} น.`
}

/** ยอดที่ลูกค้ากรอกเอง → ตัวเลข หรือข้อความผิดพลาด */
export function checkCustomAmount(raw: string, cfg: Pick<OfferingPublicConfig, 'minAmount' | 'maxAmount'>): { amount: number; error: string } {
  const n = Number(String(raw || '').replace(/[,\s]/g, ''))
  if (!Number.isFinite(n) || n <= 0) return { amount: 0, error: 'กรุณาระบุจำนวนเงิน' }
  const amount = Math.round(n * 100) / 100
  if (amount < cfg.minAmount || amount > cfg.maxAmount) {
    return { amount: 0, error: `ระบุได้ ${cfg.minAmount.toLocaleString('th-TH')} – ${cfg.maxAmount.toLocaleString('th-TH')} บาท` }
  }
  return { amount, error: '' }
}

export function statusChip(doc: Pick<OfferingDoc, 'status' | 'paid' | 'done'>): { text: string; ok: boolean } {
  if (doc.status === 'ยืนยันแล้ว') return { text: 'สำเร็จ', ok: true }
  if (doc.status === 'ยกเลิก') return { text: 'ยกเลิกแล้ว', ok: false }
  if (doc.status === 'รอยืนยัน') return { text: 'รอตรวจสอบ', ok: false }
  if (doc.paid) return { text: 'ชำระแล้ว', ok: true }
  return { text: 'รอชำระ', ok: false }
}

/** ต้องชำระภายใน 24 ชม. หลังสร้างใบ — ตรงกับ OFFERING_PAY_WINDOW_MS ฝั่ง CF (ไม่ชำระ = ยกเลิกอัตโนมัติ) */
export const OFFERING_PAY_WINDOW_MS = 24 * 60 * 60 * 1000

export function offeringDueAt(doc: Pick<OfferingDoc, 'offeringAt'>): number {
  const t = Date.parse(doc.offeringAt || '')
  return Number.isFinite(t) ? t + OFFERING_PAY_WINDOW_MS : 0
}

export function isOfferingCancelled(doc: Pick<OfferingDoc, 'status'>): boolean {
  return doc.status === 'ยกเลิก'
}

export type OfferingStep = { label: string; state: 'done' | 'now' | 'todo' | 'bad' }
/** ขั้นตอน 4 ขั้น: ร่วมถวาย · ชำระเงิน · ตรวจสอบ · อนุโมทนา */
export function offeringSteps(doc: Pick<OfferingDoc, 'status' | 'paid' | 'done'>, sent = false): OfferingStep[] {
  const confirmed = doc.status === 'ยืนยันแล้ว'
  const reviewing = !confirmed && (doc.status === 'รอยืนยัน' || sent)
  const paid = doc.paid || reviewing || confirmed
  if (isOfferingCancelled(doc)) {
    return [{ label: 'ร่วมถวาย', state: 'done' }, { label: 'ชำระเงิน', state: 'bad' }, { label: 'ตรวจสอบ', state: 'todo' }, { label: 'อนุโมทนา', state: 'todo' }]
  }
  return [
    { label: 'ร่วมถวาย', state: 'done' },
    { label: 'ชำระเงิน', state: paid ? 'done' : 'now' },
    { label: 'ตรวจสอบ', state: confirmed ? 'done' : paid ? 'now' : 'todo' },
    { label: 'อนุโมทนา', state: confirmed ? 'done' : 'todo' },
  ]
}

/** นับถอยหลัง "1 วัน 23:59:48" / "23:59:48" */
export function formatCountdown(ms: number): string {
  if (ms <= 0) return '00:00:00'
  const s = Math.floor(ms / 1000)
  const d = Math.floor(s / 86400)
  const hh = String(Math.floor((s % 86400) / 3600)).padStart(2, '0')
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0')
  const ss = String(s % 60).padStart(2, '0')
  return `${d ? `${d} วัน ` : ''}${hh}:${mm}:${ss}`
}
